import { mapState, mapGetters } from 'vuex'
import RootState from '@vue-storefront/core/types/RootState'
import toString from 'lodash-es/toString'
import { mapActions } from 'vuex'

export const Payment = {
  name: 'Payment',
  data () {
    return {
      isFilled: false,
      payment: this.$store.state.checkout.paymentDetails,
      generateInvoice: false,
      sendToShippingAddress: true,
      sendToBillingAddress: false
    }
  },
  computed: {
    ...mapState({
      currentUser: (state: RootState) => state.user.current,
      shippingDetails: (state: RootState) => state.checkout.shippingDetails
    }),
    ...mapGetters({
      paymentMethods: 'payment/paymentMethods',
      paymentDetails: 'checkout/getPaymentDetails',
      personalDetails: 'checkout/getPersonalDetails',
      isVirtualCart: 'cart/isVirtualCart',
      isUserValidationFailed: 'ipay-payment-user/isUserValidationFailed',
      getPhoneNumber: 'user/getPhoneNumber',
      getAllowedCountries: 'configs/getAllowedCountries',
      getTotalSubtotalPrice: 'cart/getTotalSubtotalPrice',
      getTotalDeliveryPrice: 'cart/getTotalDeliveryPrice'
    }),
    isFeeAdded () {
      return this.paymentDetails && ['ipay_masterpass', 'ipay_applepay', 'ipay_gpay'].includes(this.paymentDetails.paymentMethod)
    },
    getCustomerId () {
      return this.$store.state.user.current ? this.$store.state.user.current.id : null
    },
    amountToPay () {
      const deliveryPrice = this.getTotalDeliveryPrice()
      return deliveryPrice == null ? null : (this.getTotalSubtotalPrice + deliveryPrice) * 100
    }
  },
  created () {
    if (!this.payment.paymentMethod || this.notInMethods(this.payment.paymentMethod)) {
      this.payment.paymentMethod = this.paymentMethods.length > 0 ? this.paymentMethods[0].code : 'cash'
    }
  },
  mounted () {
    if (this.payment.firstName) {
      this.initializeBillingAddress()
    } else {
      if (this.payment.company) {
        this.generateInvoice = true
      }
    }
    this.changePaymentMethod()
    if (this.paymentDetails.paymentMethod) {
      this.payment = this.paymentDetails
    }
    this.copyShippingToBillingAddress()
    this.sendDataToCheckout()
    this.quoteFee()
  },
  watch: {
    shippingDetails: {
      handler () {
        if (this.sendToShippingAddress) {
          this.copyShippingToBillingAddress()
        }
      },
      deep: true
    },
    sendToShippingAddress: {
      handler () {
        this.useShippingAddress()
      }
    },
    sendToBillingAddress: {
      handler () {
        this.useBillingAddress()
      }
    },
    generateInvoice: {
      handler () {
        this.useGenerateInvoice()
      }
    },
    payment: {
      deep: true,
      handler: function () {
        this.sendDataToCheckout()
      }
    },
    'payment.paymentMethod': 'quoteFee',
    amountToPay: 'quoteFee'
  },
  methods: {
    ...mapActions({
      calculateFee: 'ipay-payment-user/calculateFee',
      resetFee: 'ipay-payment-user/resetFee'
    }),
    quoteFee () {
      if (!this.isFeeAdded) {
        this.resetFee()
      } else if (this.amountToPay != null) {
        this.calculateFee({
          phoneNumber: this.getPhoneNumber(true),
          customerId: this.getCustomerId,
          amount: this.amountToPay,
          method: this.paymentDetails.paymentMethod.replace('ipay_', '')
        })
      }
    },
    sendDataToCheckout () {
      this.$bus.$emit('checkout-after-paymentDetails', this.payment, this.$v)
      this.isFilled = true
      this.$forceUpdate();
    },
    edit () {
      if (this.isFilled) {
        this.$bus.$emit('checkout-before-edit', 'payment')
      }
    },
    hasBillingData () {
      if (this.currentUser) {
        if (this.currentUser.hasOwnProperty('default_billing')) {
          return true
        }
      }
      return false
    },
    initializeBillingAddress () {
      let initialized = false
      if (this.currentUser) {
        if (this.currentUser.hasOwnProperty('default_billing')) {
          let id = this.currentUser.default_billing
          let addresses = this.currentUser.addresses
          for (let i = 0; i < addresses.length; i++) {
            if (toString(addresses[i].id) === toString(id)) {
              this.payment = {
                firstName: addresses[i].firstname,
                lastName: addresses[i].lastname,
                company: addresses[i].company,
                country: addresses[i].country,
                state: addresses[i].state ? addresses[i].state : '',
                city: addresses[i].city,
                streetAddress: addresses[i].street[0],
                houseNumber: addresses[i].street[1],
                apartmentNumber: addresses[i].street[2],
                zipCode: addresses[i].postcode,
                taxId: addresses[i].vat_id,
                phoneNumber: addresses[i].telephone,
                paymentMethod: this.paymentMethods[0].code
              }
              this.generateInvoice = true
              this.sendToBillingAddress = true
              initialized = true
            }
          }
        }
      }
      if (!initialized) {
        this.payment = {
          firstName: '',
          lastName: '',
          company: '',
          country: '',
          state: '',
          city: '',
          streetAddress: '',
          houseNumber: '',
          apartmentNumber: '',
          postcode: '',
          zipCode: '',
          phoneNumber: '',
          taxId: '',
          paymentMethod: this.paymentMethods.length > 0 ? this.paymentMethods[0].code : ''
        }
      }
    },
    useShippingAddress () {
      if (this.sendToShippingAddress) {
        this.copyShippingToBillingAddress()
        this.sendToBillingAddress = false
      }

      if (!this.sendToBillingAddress && !this.sendToShippingAddress) {
        this.payment = this.$store.state.checkout.paymentDetails
      }
    },
    copyShippingToBillingAddress () {
      this.payment = {
        firstName: this.shippingDetails.firstName,
        lastName: this.shippingDetails.lastName,
        country: this.shippingDetails.country,
        state: this.shippingDetails.state,
        city: this.shippingDetails.city,
        streetAddress: this.shippingDetails.streetAddress,
        houseNumber: this.shippingDetails.houseNumber,
        apartmentNumber: this.shippingDetails.apartmentNumber,
        zipCode: this.shippingDetails.zipCode,
        phoneNumber: this.shippingDetails.phoneNumber,
        paymentMethod: this.payment.paymentMethod !== '' ? this.payment.paymentMethod : '',
        banknoteDenomination: this.payment.banknoteDenomination > 0 ? this.payment.banknoteDenomination : ''
      }
    },
    useBillingAddress () {
      if (this.sendToBillingAddress) {
        let id = this.currentUser.default_billing
        let addresses = this.currentUser.addresses
        for (let i = 0; i < addresses.length; i++) {
          if (toString(addresses[i].id) === toString(id)) {
            this.payment = {
              firstName: addresses[i].firstname,
              lastName: addresses[i].lastname,
              company: addresses[i].company,
              country: addresses[i].country,
              state: addresses[i].state ? addresses[i].state : '',
              city: addresses[i].city,
              streetAddress: addresses[i].street[0],
              houseNumber: addresses[i].street[1],
              apartmentNumber: addresses[i].street[2],
              zipCode: addresses[i].postcode,
              taxId: addresses[i].vat_id,
              phoneNumber: addresses[i].telephone,
              paymentMethod: this.paymentMethods.length > 0 ? this.paymentMethods[0].code : ''
            }
            this.generateInvoice = true
          }
        }
        this.sendToShippingAddress = false
      }

      if (!this.sendToBillingAddress && !this.sendToShippingAddress) {
        this.payment = this.$store.state.checkout.paymentDetails
        this.generateInvoice = false
      }
    },
    useGenerateInvoice () {
      if (!this.generateInvoice) {
        this.payment.company = ''
        this.payment.taxId = ''
      }
    },
    getCountryName () {
      return this.getAllowedCountries.find(c => c.code === this.payment.country) || ''
    },
    getPaymentMethod () {
      for (let i = 0; i < this.paymentMethods.length; i++) {
        if (this.paymentMethods[i].code === this.payment.paymentMethod) {
          return {
            title: this.paymentMethods[i].title ? this.paymentMethods[i].title : this.paymentMethods[i].name
          }
        }
      }
      return {
        name: ''
      }
    },
    notInMethods (method) {
      let availableMethods = this.paymentMethods
      if (availableMethods.find(item => item.code === method)) {
        return false
      }
      return true
    },
    changePaymentMethod () {
      // reset the additional payment method component container if exists.
      if (document.getElementById('checkout-order-review-additional-container')) {
        document.getElementById('checkout-order-review-additional-container').innerHTML = '<div id="checkout-order-review-additional">&nbsp;</div>' // reset
      }

      // Let anyone listening know that we've changed payment method, usually a payment extension.
      this.$bus.$emit('checkout-payment-method-changed', this.payment.paymentMethod)
    }
  }
}
